import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDateTime as formatDateTimeUtc } from '../utils/dates';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowLeft,
  FaSyncAlt,
  FaExclamationTriangle,
  FaSave,
  FaTimes,
  FaEdit,
  FaPlus,
  FaTrash,
  FaKey,
  FaEnvelope,
  FaUsers,
  FaCheck,
  FaRobot,
  FaGlobe,
  FaChevronDown,
  FaChevronRight,
  FaEye,
  FaEyeSlash,
  FaShieldAlt,
  FaUniversity,
  FaUndo,
} from 'react-icons/fa';
import { SiMicrosoftoutlook } from 'react-icons/si';
import getCarrierLogo from '../utils/carrierLogos';
import CarrierRecipes from './CarrierRecipes';
import './CarrierDetail.css';
import Button from './UI/Button';

// Destination scope of a credential account: null = any shipment,
// { countries: ['FR'], invert } = only these destinations (or all EXCEPT them).
const scopeToForm = (scope) => {
  if (!scope || !scope.countries || scope.countries.length === 0) {
    return { scope_preset: 'any', scope_countries: '', scope_invert: false };
  }
  const isFrOnly = scope.countries.length === 1 && scope.countries[0] === 'FR';
  if (isFrOnly && !scope.invert) return { scope_preset: 'fr', scope_countries: '', scope_invert: false };
  if (isFrOnly && scope.invert) return { scope_preset: 'intl', scope_countries: '', scope_invert: false };
  return { scope_preset: 'custom', scope_countries: scope.countries.join(', '), scope_invert: !!scope.invert };
};

const formToScope = (form) => {
  if (form.scope_preset === 'fr') return { countries: ['FR'], invert: false };
  if (form.scope_preset === 'intl') return { countries: ['FR'], invert: true };
  if (form.scope_preset === 'custom') {
    const countries = (form.scope_countries || '')
      .split(',')
      .map((c) => c.trim().toUpperCase())
      .filter(Boolean);
    return countries.length ? { countries, invert: !!form.scope_invert } : null;
  }
  return null;
};

// Backend timestamps are naive UTC strings (KI-110): shared parser.
const formatDateTime = (value) => formatDateTimeUtc(value);

// Subject of the e-mails sent to the carrier (backend
// app/services/carrier_email_subject.py renders the real one; this preview
// fills the variables with an example).
export const DEFAULT_CARRIER_SUBJECT = '{transporteur} – Colis {suivi} – {objet}';
const SUBJECT_EXAMPLE = {
  transporteur: null, carrier: null, suivi: '878301508190', tracking: '878301508190',
  objet: 'Demande de localisation du colis', subject: 'Demande de localisation du colis',
  commande: '1183266', order: '1183266',
};

export const previewCarrierSubject = (template, carrierName) => {
  const chosen = (template || '').trim() || DEFAULT_CARRIER_SUBJECT;
  return chosen.replace(/\{([^{}]*)\}/g, (match, name) => {
    const key = name.trim().toLowerCase();
    if (!(key in SUBJECT_EXAMPLE)) return match;
    return SUBJECT_EXAMPLE[key] === null ? (carrierName || 'FedEx') : SUBJECT_EXAMPLE[key];
  });
};

const CarrierDetail = () => {
  const { carrierId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [carrier, setCarrier] = useState(null);
  // Mondial Relay enseignes sync (carrier page → account enseignes → client aliases)
  const [enseigneSync, setEnseigneSync] = useState({ loading: false, error: null, data: null, applied: null });
  const [enseigneChoices, setEnseigneChoices] = useState({});
  const [carrierLoading, setCarrierLoading] = useState(true);
  const [carrierError, setCarrierError] = useState(null);

  const [isEditingCarrier, setIsEditingCarrier] = useState(false);
  const [carrierForm, setCarrierForm] = useState({});
  const [carrierSaving, setCarrierSaving] = useState(false);
  const [carrierSaveError, setCarrierSaveError] = useState(null);

  const [credentials, setCredentials] = useState([]);
  const [credentialsLoading, setCredentialsLoading] = useState(true);

  const [ednClients, setEdnClients] = useState([]);

  const [showAddCredential, setShowAddCredential] = useState(false);
  const [newCredential, setNewCredential] = useState({
    username: '',
    password: '',
    label: '',
    client_ids: [],
    additional_fields: {},
    scope_preset: 'any',
    scope_countries: '',
    scope_invert: false,
  });
  const [addingCredential, setAddingCredential] = useState(false);
  const [addCredentialError, setAddCredentialError] = useState(null);

  const [editingCredentialId, setEditingCredentialId] = useState(null);
  const [editCredentialForm, setEditCredentialForm] = useState({});
  const [savingCredential, setSavingCredential] = useState(false);
  const [editCredentialError, setEditCredentialError] = useState(null);

  const [showContactRules, setShowContactRules] = useState(false);

  // Per-carrier OTP mailbox (portal login verification codes)
  const [otpMailbox, setOtpMailbox] = useState(null);
  const [otpMailboxLoading, setOtpMailboxLoading] = useState(true);
  const [showOtpImapForm, setShowOtpImapForm] = useState(false);
  const [otpImapForm, setOtpImapForm] = useState({
    email: '',
    password: '',
    imap_server: 'imap.gmail.com',
    imap_port: 993,
  });
  const [showOtpPassword, setShowOtpPassword] = useState(false);
  const [otpSaving, setOtpSaving] = useState(false);
  const [otpError, setOtpError] = useState(null);
  const [otpNotice, setOtpNotice] = useState(null);
  const [otpOutlookLoading, setOtpOutlookLoading] = useState(false);
  const [otpLinkableAccounts, setOtpLinkableAccounts] = useState([]);
  const [otpLinking, setOtpLinking] = useState(null);

  // Claim identity (FedEx/TNT claim forms): contact + bank pre-filled on the
  // carrier claim form. Bank is stored encrypted server-side; GET returns it
  // masked (iban_last4), so editing requires re-entering the IBAN.
  const emptyClaimForm = {
    contact_as: 'sender',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    account_number: '',
    company_name: '',
    address_line1: '',
    address_line2: '',
    postal_code: '',
    city: '',
    iban: '',
    bic: '',
    bank_name: '',
    account_holder_name: '',
  };
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [claimForm, setClaimForm] = useState(emptyClaimForm);
  const [claimSaving, setClaimSaving] = useState(false);
  const [claimNotice, setClaimNotice] = useState(null);

  const authHeaders = useMemo(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
    if (!token) return null;
    return { Authorization: `Bearer ${token}` };
  }, []);

  useEffect(() => {
    if (!authHeaders) navigate('/');
  }, [authHeaders, navigate]);

  // Portal attachment upload ("connect" mode) is only implemented for Mondial Relay.
  const isMondialRelay = (carrier?.name || '').toLowerCase().includes('mondial');

  // Claim-form carriers (FedEx form also files TNT claims) get the claim
  // identity card; matches the backend create_fedex_ticket allow-list.
  const isClaimFormCarrier = /^(fedex|tnt)$/i.test((carrier?.name || '').trim());

  const syncEnseignes = useCallback(async () => {
    setEnseigneSync({ loading: true, error: null, data: null, applied: null });
    try {
      const response = await axios.post(
        `/api/logistics/carriers/${carrierId}/mondial-relay/enseignes/sync`, {}, { headers: authHeaders }
      );
      const data = response.data || {};
      const choices = {};
      (data.enseignes || []).forEach((row) => {
        if (row.status === 'proposed' && row.proposal) choices[row.enseigne] = row.proposal.client_id;
      });
      setEnseigneChoices(choices);
      setEnseigneSync({ loading: false, error: null, data, applied: null });
      fetchCarrier();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setEnseigneSync({
        loading: false,
        error: typeof detail === 'string' && detail ? detail : t('carrierDetail.enseignesSyncError', 'La synchronisation des enseignes a échoué.'),
        data: null,
        applied: null,
      });
    }
  }, [carrierId, authHeaders, t]);  // eslint-disable-line react-hooks/exhaustive-deps

  const applyEnseignes = useCallback(async () => {
    const mappings = Object.entries(enseigneChoices)
      .filter(([, clientId]) => clientId)
      .map(([enseigne, clientId]) => ({ enseigne, client_id: clientId }));
    if (mappings.length === 0) return;
    setEnseigneSync((s) => ({ ...s, loading: true, error: null }));
    try {
      const response = await axios.post(
        `/api/logistics/carriers/${carrierId}/mondial-relay/enseignes/apply`, { mappings }, { headers: authHeaders }
      );
      setEnseigneSync((s) => ({ ...s, loading: false, applied: response.data }));
      fetchEdnClients();
      syncEnseignes();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setEnseigneSync((s) => ({
        ...s, loading: false,
        error: typeof detail === 'string' && detail ? detail : t('carrierDetail.enseignesApplyError', 'L’enregistrement des correspondances a échoué.'),
      }));
    }
  }, [carrierId, authHeaders, enseigneChoices, t]);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCarrier = useCallback(async () => {
    if (!authHeaders) return;
    setCarrierLoading(true);
    setCarrierError(null);
    try {
      const response = await axios.get('/api/logistics/carriers', { headers: authHeaders });
      const found = response.data.find((c) => c.id === carrierId);
      if (found) {
        setCarrier(found);
      } else {
        setCarrierError(t('carrierDetail.notFound', 'Transporteur introuvable'));
      }
    } catch (err) {
      setCarrierError(err.response?.data?.detail || err.message || 'Failed to load carrier');
    } finally {
      setCarrierLoading(false);
    }
  }, [authHeaders, carrierId, t]);

  const fetchCredentials = useCallback(async () => {
    if (!authHeaders) return;
    setCredentialsLoading(true);
    try {
      const response = await axios.get(`/api/logistics/carrier-credentials/${carrierId}/all`, {
        headers: authHeaders,
      });
      setCredentials(Array.isArray(response.data) ? response.data : []);
    } catch {
      setCredentials([]);
    } finally {
      setCredentialsLoading(false);
    }
  }, [authHeaders, carrierId]);

  const fetchEdnClients = useCallback(async () => {
    if (!authHeaders) return;
    try {
      const response = await axios.get('/api/logistics/clients-edn', { headers: authHeaders });
      setEdnClients(Array.isArray(response.data) ? response.data : []);
    } catch {
      setEdnClients([]);
    }
  }, [authHeaders]);

  const fetchOtpMailbox = useCallback(async () => {
    if (!authHeaders) return;
    setOtpMailboxLoading(true);
    try {
      const response = await axios.get(`/api/logistics/carriers/${carrierId}/otp-mailbox`, {
        headers: authHeaders,
      });
      setOtpMailbox(response.data);
    } catch {
      setOtpMailbox(null);
    } finally {
      setOtpMailboxLoading(false);
    }
    // Accounts already connected for ticketing that can double as the OTP
    // mailbox (no re-authentication needed).
    try {
      const linkable = await axios.get(
        `/api/logistics/carriers/${carrierId}/otp-mailbox/linkable-accounts`,
        { headers: authHeaders },
      );
      setOtpLinkableAccounts(linkable.data?.accounts || []);
    } catch {
      setOtpLinkableAccounts([]);
    }
  }, [authHeaders, carrierId]);

  useEffect(() => {
    fetchCarrier();
    fetchCredentials();
    fetchEdnClients();
    fetchOtpMailbox();
  }, [fetchCarrier, fetchCredentials, fetchEdnClients, fetchOtpMailbox]);

  // Landing back from the Outlook OAuth redirect (?otp_status=success|failure)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const otpStatus = params.get('otp_status');
    if (!otpStatus) return;
    setOtpNotice(
      otpStatus === 'success'
        ? { type: 'success', text: t('carrierDetail.otpOutlookConnected', 'Boîte Outlook connectée pour les codes OTP.') }
        : { type: 'error', text: t('carrierDetail.otpOutlookFailed', "La connexion Outlook a échoué. Réessayez ou utilisez une boîte Gmail avec mot de passe d'application.") },
    );
    params.delete('otp_status');
    const query = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  }, [t]);

  const handleConnectOtpOutlook = async () => {
    if (!authHeaders) return;
    setOtpOutlookLoading(true);
    setOtpError(null);
    try {
      const response = await axios.get(
        `/api/logistics/carriers/${carrierId}/otp-mailbox/outlook-connect`,
        { headers: authHeaders },
      );
      window.location.href = response.data.authorization_url;
    } catch (err) {
      setOtpError(err.response?.data?.detail || err.message);
      setOtpOutlookLoading(false);
    }
  };

  const handleSaveOtpImap = async () => {
    if (!authHeaders) return;
    setOtpSaving(true);
    setOtpError(null);
    try {
      const response = await axios.put(
        `/api/logistics/carriers/${carrierId}/otp-mailbox`,
        otpImapForm,
        { headers: authHeaders },
      );
      setOtpMailbox(response.data);
      setShowOtpImapForm(false);
      setOtpImapForm({ email: '', password: '', imap_server: 'imap.gmail.com', imap_port: 993 });
      setOtpNotice({ type: 'success', text: t('carrierDetail.otpImapConnected', 'Boîte mail connectée et vérifiée pour les codes OTP.') });
    } catch (err) {
      setOtpError(err.response?.data?.detail || err.message);
    } finally {
      setOtpSaving(false);
    }
  };

  const handleDisconnectOtpMailbox = async () => {
    if (!authHeaders) return;
    if (!window.confirm(t('carrierDetail.otpDisconnectConfirm', 'Déconnecter la boîte mail OTP de ce transporteur ?'))) return;
    setOtpError(null);
    try {
      await axios.delete(`/api/logistics/carriers/${carrierId}/otp-mailbox`, { headers: authHeaders });
      setOtpMailbox({ configured: false, provider: null, email: null, connected_at: null });
      setOtpNotice(null);
    } catch (err) {
      setOtpError(err.response?.data?.detail || err.message);
    }
  };

  const handleLinkOtpAccount = async (account) => {
    if (!authHeaders) return;
    setOtpLinking(`${account.provider}:${account.email}`);
    setOtpError(null);
    try {
      const response = await axios.post(
        `/api/logistics/carriers/${carrierId}/otp-mailbox/link`,
        { provider: account.provider, email: account.email },
        { headers: authHeaders },
      );
      setOtpMailbox(response.data);
      setOtpNotice({
        type: 'success',
        text: t('carrierDetail.otpLinked', 'Compte connecté réutilisé pour les codes OTP.'),
      });
    } catch (err) {
      setOtpError(err.response?.data?.detail || err.message);
    } finally {
      setOtpLinking(null);
    }
  };

  const handleBack = () => navigate('/carriers-instructions');

  // Carrier editing
  const handleStartEditCarrier = () => {
    setCarrierForm({
      name: carrier.name || '',
      emails: carrier.emails || [],
      contact_rules: carrier.contact_rules || '',
      message_instructions: carrier.message_instructions || '',
      email_subject_template: carrier.email_subject_template || '',
      automation: carrier.automation || false,
      is_mail: carrier.is_mail || false,
      cc_emails: carrier.cc_emails || [],
      default_enseigne: carrier.default_enseigne || '',
      email_routing_rules: JSON.stringify(carrier.email_routing_rules || [], null, 2),
      form_tracking_patterns: (carrier.form_tracking_patterns || []).join('\n'),
      form_tool: carrier.form_tool || '',
      web2case_url: carrier.web2case_url || '',
      // Key/value rows edited in the form; sent back as a map (empty map clears).
      form_defaults: Object.entries(carrier.form_defaults || {}).map(([key, value]) => ({ key, value })),
    });
    setCarrierSaveError(null);
    setIsEditingCarrier(true);
  };

  const openClaimForm = () => {
    const contact = carrier?.claim_contact || {};
    const bank = carrier?.claim_bank || {};
    setClaimForm({
      ...emptyClaimForm,
      ...contact,
      // IBAN is never returned by the API: re-entered on every edit.
      iban: '',
      bic: bank.bic || '',
      bank_name: bank.bank_name || '',
      account_holder_name: bank.account_holder_name || '',
    });
    setClaimNotice(null);
    setShowClaimForm(true);
  };

  const handleSaveClaim = async () => {
    if (!authHeaders) return;
    setClaimSaving(true);
    setClaimNotice(null);
    try {
      const contact = {
        contact_as: claimForm.contact_as || 'sender',
        first_name: claimForm.first_name.trim(),
        last_name: claimForm.last_name.trim(),
        email: claimForm.email.trim(),
        phone_number: claimForm.phone_number.trim(),
        account_number: claimForm.account_number.trim(),
        company_name: claimForm.company_name.trim(),
        address_line1: claimForm.address_line1.trim(),
        address_line2: claimForm.address_line2.trim(),
        postal_code: claimForm.postal_code.trim(),
        city: claimForm.city.trim(),
      };
      const payload = { claim_contact: contact };
      const bankTouched = ['iban', 'bic', 'bank_name', 'account_holder_name']
        .some((f) => (claimForm[f] || '').trim());
      if (bankTouched) {
        if (!(claimForm.iban || '').trim()) {
          setClaimNotice({
            type: 'error',
            text: t('carrierDetail.claimIbanRequired', "Ressaisissez l'IBAN complet pour modifier les coordonnées bancaires (il n'est jamais réaffiché)."),
          });
          setClaimSaving(false);
          return;
        }
        payload.claim_bank = {
          iban: claimForm.iban.trim(),
          bic: claimForm.bic.trim(),
          bank_name: claimForm.bank_name.trim(),
          account_holder_name: claimForm.account_holder_name.trim(),
        };
      }
      await axios.put(`/api/logistics/carriers/${carrierId}`, payload, { headers: authHeaders });
      setShowClaimForm(false);
      setClaimNotice({ type: 'success', text: t('carrierDetail.claimSaved', 'Identité de réclamation enregistrée.') });
      fetchCarrier();
    } catch (err) {
      setClaimNotice({ type: 'error', text: err.response?.data?.detail || err.message || 'Erreur' });
    } finally {
      setClaimSaving(false);
    }
  };

  const handleClearClaim = async () => {
    if (!authHeaders) return;
    setClaimSaving(true);
    setClaimNotice(null);
    try {
      // All-empty blocks clear the stored identity server-side.
      await axios.put(
        `/api/logistics/carriers/${carrierId}`,
        { claim_contact: emptyClaimForm, claim_bank: { iban: '', bic: '', bank_name: '', account_holder_name: '' } },
        { headers: authHeaders },
      );
      setShowClaimForm(false);
      setClaimNotice({ type: 'success', text: t('carrierDetail.claimCleared', 'Identité de réclamation supprimée.') });
      fetchCarrier();
    } catch (err) {
      setClaimNotice({ type: 'error', text: err.response?.data?.detail || err.message || 'Erreur' });
    } finally {
      setClaimSaving(false);
    }
  };

  const handleCancelEditCarrier = () => {
    setIsEditingCarrier(false);
    setCarrierSaveError(null);
  };

  const handleSaveCarrier = async () => {
    if (!authHeaders) return;
    setCarrierSaving(true);
    setCarrierSaveError(null);
    try {
      let parsedRoutingRules = null;
      try {
        const parsed = JSON.parse(carrierForm.email_routing_rules);
        if (Array.isArray(parsed) && parsed.length > 0) parsedRoutingRules = parsed;
      } catch { /* keep null */ }

      await axios.put(
        `/api/logistics/carriers/${carrierId}`,
        {
          name: carrierForm.name,
          emails: carrierForm.emails,
          contact_rules: carrierForm.contact_rules,
          // Always sent (backend ignores null): '' clears the instructions.
          message_instructions: carrierForm.message_instructions || '',
          // Always sent: '' restores the default subject.
          email_subject_template: (carrierForm.email_subject_template || '').trim(),
          automation: carrierForm.automation,
          is_mail: carrierForm.is_mail || false,
          cc_emails: carrierForm.cc_emails,
          // Always sent: '' clears the tenant-level default enseigne.
          default_enseigne: (carrierForm.default_enseigne || '').trim(),
          email_routing_rules: parsedRoutingRules,
          // Always sent: empty list/string clear the form routing on the backend.
          form_tracking_patterns: (carrierForm.form_tracking_patterns || '')
            .split('\n')
            .map((p) => p.trim())
            .filter(Boolean),
          form_tool: (carrierForm.form_tool || '').trim(),
          // Always sent: '' clears the per-tenant Web2Case URL.
          web2case_url: (carrierForm.web2case_url || '').trim(),
          // Always sent: an empty map clears the form defaults on the backend.
          form_defaults: Object.fromEntries(
            (carrierForm.form_defaults || [])
              .map((row) => [(row.key || '').trim(), (row.value || '').trim()])
              .filter(([key, value]) => key && value),
          ),
        },
        { headers: authHeaders },
      );
      await fetchCarrier();
      setIsEditingCarrier(false);
    } catch (err) {
      setCarrierSaveError(err.response?.data?.detail || err.message || 'Failed to save');
    } finally {
      setCarrierSaving(false);
    }
  };

  const handleAddEmailField = () => {
    setCarrierForm((f) => ({ ...f, emails: [...f.emails, ''] }));
  };

  const handleEmailChange = (index, value) => {
    setCarrierForm((f) => {
      const updated = [...f.emails];
      updated[index] = value;
      return { ...f, emails: updated };
    });
  };

  const handleRemoveEmail = (index) => {
    setCarrierForm((f) => {
      const updated = [...f.emails];
      updated.splice(index, 1);
      return { ...f, emails: updated };
    });
  };

  // Credential management
  const handleAddCredential = async () => {
    if (!authHeaders) return;
    setAddingCredential(true);
    setAddCredentialError(null);
    try {
      await axios.post(
        '/api/logistics/carrier-credentials',
        {
          carrier_id: carrierId,
          username: newCredential.username,
          password: newCredential.password,
          label: newCredential.label || newCredential.username,
          client_ids: newCredential.client_ids,
          additional_fields: newCredential.additional_fields,
          scope: formToScope(newCredential),
        },
        { headers: authHeaders },
      );
      setShowAddCredential(false);
      setNewCredential({ username: '', password: '', label: '', client_ids: [], additional_fields: {}, scope_preset: 'any', scope_countries: '', scope_invert: false });
      await fetchCredentials();
    } catch (err) {
      setAddCredentialError(err.response?.data?.detail || err.message || 'Failed to add');
    } finally {
      setAddingCredential(false);
    }
  };

  const handleStartEditCredential = (cred) => {
    setEditingCredentialId(cred.id);
    setEditCredentialForm({
      username: cred.username || '',
      password: '',
      label: cred.label || '',
      client_ids: cred.client_ids || [],
      ...scopeToForm(cred.scope),
    });
    setEditCredentialError(null);
  };

  const handleCancelEditCredential = () => {
    setEditingCredentialId(null);
    setEditCredentialError(null);
  };

  const handleSaveCredential = async (credId) => {
    if (!authHeaders) return;
    setSavingCredential(true);
    setEditCredentialError(null);
    try {
      const payload = {
        username: editCredentialForm.username || undefined,
        label: editCredentialForm.label || undefined,
        client_ids: editCredentialForm.client_ids,
        scope: formToScope(editCredentialForm),
      };
      if (editCredentialForm.password) {
        payload.password = editCredentialForm.password;
      }
      await axios.put(`/api/logistics/carrier-credentials/${credId}`, payload, {
        headers: authHeaders,
      });
      setEditingCredentialId(null);
      await fetchCredentials();
    } catch (err) {
      setEditCredentialError(err.response?.data?.detail || err.message || 'Failed to save');
    } finally {
      setSavingCredential(false);
    }
  };

  const handleDeleteCredential = async (cred) => {
    if (!authHeaders) return;
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      t('carrierDetail.deleteCredentialConfirm',
        'Supprimer le compte « {{label}} » ? L\'automatisation ne pourra plus utiliser ces identifiants.',
        { label: cred.label || cred.username }),
    );
    if (!confirmed) return;
    try {
      await axios.delete(`/api/logistics/carrier-credentials/${cred.id}`, {
        headers: authHeaders,
      });
      await fetchCredentials();
    } catch (err) {
      console.error('Failed to delete credential:', err);
    }
  };

  const toggleClientInList = (clientId, list, setter) => {
    if (list.includes(clientId)) {
      setter(list.filter((id) => id !== clientId));
    } else {
      setter([...list, clientId]);
    }
  };

  const getClientName = (clientId) => {
    const found = ednClients.find((c) => c.id === clientId);
    return found ? found.name : clientId;
  };

  const handleRefresh = () => {
    fetchCarrier();
    fetchCredentials();
  };

  if (!authHeaders) return null;

  const definitionRow = (label, value) => (
    <div className="cd-def-row">
      <span className="cd-def-label">{label}</span>
      <span className="cd-def-value">{value}</span>
    </div>
  );

  const scopeLabel = (scope) => {
    const form = scopeToForm(scope);
    if (form.scope_preset === 'fr') return t('carrierDetail.scopeFrance', 'France uniquement');
    if (form.scope_preset === 'intl') return t('carrierDetail.scopeInternational', 'International (hors France)');
    return (scope.invert ? t('carrierDetail.scopeExceptPrefix', 'Tout sauf : ') : '') + scope.countries.join(', ');
  };

  const credentialScopeFields = (form, setForm) => (
    <div className="cd-form-group">
      <label>{t('carrierDetail.destinationScope', 'Périmètre de destination')}</label>
      <select
        value={form.scope_preset || 'any'}
        onChange={(e) => setForm((f) => ({ ...f, scope_preset: e.target.value }))}
        className="cd-input"
      >
        <option value="any">{t('carrierDetail.scopeAny', 'Toutes destinations (défaut)')}</option>
        <option value="fr">{t('carrierDetail.scopeFrance', 'France uniquement')}</option>
        <option value="intl">{t('carrierDetail.scopeInternational', 'International (hors France)')}</option>
        <option value="custom">{t('carrierDetail.scopeCustom', 'Liste de pays personnalisée...')}</option>
      </select>
      {form.scope_preset === 'custom' && (
        <>
          <input
            type="text"
            value={form.scope_countries}
            onChange={(e) => setForm((f) => ({ ...f, scope_countries: e.target.value }))}
            placeholder={t('carrierDetail.scopeCountriesPlaceholder', 'Codes pays, ex. FR, BE, LU')}
            className="cd-input"
          />
          <label className="cd-inline-toggle">
            <input
              type="checkbox"
              checked={!!form.scope_invert}
              onChange={(e) => setForm((f) => ({ ...f, scope_invert: e.target.checked }))}
            />
            {t('carrierDetail.scopeInvert', 'Toutes les destinations SAUF ces pays')}
          </label>
        </>
      )}
    </div>
  );

  const clientChips = (selectedIds, onToggle) => (
    <div className="cd-client-chips">
      {ednClients.map((client) => (
        <button
          key={client.id}
          type="button"
          className={`cd-client-chip ${selectedIds.includes(client.id) ? 'selected' : ''}`}
          onClick={() => onToggle(client.id)}
        >
          {client.name}
          {selectedIds.includes(client.id) && <FaCheck aria-hidden="true" />}
        </button>
      ))}
      {ednClients.length === 0 && (
        <span className="cd-muted-text">{t('carrierDetail.noClientsAvailable', 'Aucun client disponible')}</span>
      )}
    </div>
  );

  return (
    <div className="carrier-detail-container">
      {/* Page header: back + identity + actions */}
      <header className="cd-header">
        <div className="cd-header-left">
          <button type="button" className="cd-back-button" onClick={handleBack} aria-label={t('carrierDetail.backToCarriers', 'Retour aux transporteurs')}>
            <FaArrowLeft />
          </button>
          {carrier && !carrierLoading && (
            <div className="cd-header-identity">
              {getCarrierLogo(carrier.name) && (
                <span className="cd-header-logo" aria-hidden="true">
                  <img src={getCarrierLogo(carrier.name)} alt="" />
                </span>
              )}
              <h1>{carrier.name}</h1>
              <span className={`carrier-mode-chip ${carrier.is_mail ? 'mail' : 'web'}`}>
                {carrier.is_mail
                  ? <><FaEnvelope aria-hidden="true" /> {t('carriersInstructions.modeMail', 'Email')}</>
                  : <><FaRobot aria-hidden="true" /> {t('carriersList.modePortal', 'Portail')}</>}
              </span>
              <span className={`cd-auto-state ${carrier.automation ? 'on' : 'off'}`}>
                <span className="cd-state-dot" aria-hidden="true" />
                {carrier.automation
                  ? t('clientEDNDetail.automationOn', 'Automatisation active')
                  : t('clientEDNDetail.automationOff', 'Automatisation désactivée')}
              </span>
            </div>
          )}
        </div>
        <div className="cd-header-actions">
          {/* Colissimo return-labels setup lives HERE, with its carrier — it
              only feeds the « étiquette retour » action of the ticket sidebar
              (moved out of Paramètres, Kevin 2026-10-06). */}
          {carrier && /colissimo/i.test(carrier.name || '') && (
            <button type="button" className="u-btn u-btn--secondary" onClick={() => navigate('/colissimo-params')}>
              <FaUndo />
              {t('carrierDetail.colissimoReturns', 'Étiquettes retour')}
            </button>
          )}
          {!isEditingCarrier && carrier && (
            <button type="button" className="u-btn u-btn--primary" onClick={handleStartEditCarrier}>
              <FaEdit />
              {t('carriersInstructions.editCarrier', 'Modifier')}
            </button>
          )}
          <button
            type="button"
            className="u-btn u-btn--secondary"
            onClick={handleRefresh}
            disabled={carrierLoading || credentialsLoading}
          >
            <FaSyncAlt />
            {t('carrierDetail.refresh', 'Actualiser')}
          </button>
        </div>
      </header>

      {carrierLoading ? (
        <div className="cd-card">
          <div className="cd-skeleton" aria-busy="true">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="cd-skeleton-row" />
            ))}
          </div>
        </div>
      ) : carrierError ? (
        <div className="cd-card">
          <div className="cd-state cd-state-error">
            <FaExclamationTriangle />
            <p>{carrierError}</p>
          </div>
        </div>
      ) : carrier && (
        <div className="cd-grid">
          {/* CONFIGURATION card (view or sectioned edit form) */}
          <section className="cd-card cd-config-card">
            <div className="cd-card-title">
              <h2>{t('clientEDNDetail.configuration', 'Configuration')}</h2>
              <span className="cd-card-title-meta">
                {t('common.updatedAt', 'Maj le')} {formatDateTime(carrier.updated_at)}
              </span>
            </div>

            {!isEditingCarrier ? (
              <div className="cd-config-view">
                {definitionRow(
                  t('carriersInstructions.emails', 'Emails'),
                  carrier.emails?.length > 0 ? (
                    <span className="cd-chip-list">
                      {carrier.emails.map((email) => (
                        <span key={email} className="cd-chip">{email}</span>
                      ))}
                    </span>
                  ) : carrier.is_mail ? (
                    <span className="cd-flag-warn">
                      <span className="cd-warn-dot" aria-hidden="true" />
                      {t('clientEDNDetail.noneConfigured', 'aucun')}
                    </span>
                  ) : (
                    <span className="cd-muted-dash">–</span>
                  ),
                )}

                {carrier.cc_emails?.length > 0 && definitionRow(
                  t('carrierDetail.ccEmails', 'CC'),
                  <span className="cd-chip-list">
                    {carrier.cc_emails.map((email) => (
                      <span key={email} className="cd-chip">{email}</span>
                    ))}
                  </span>,
                )}

                {definitionRow(
                  t('carrierDetail.emailSubject', 'Objet des e-mails'),
                  <span className="cd-def-text">
                    {previewCarrierSubject(carrier.email_subject_template, carrier.name)}
                    {!carrier.email_subject_template && (
                      <span className="cd-muted"> ({t('carrierDetail.emailSubjectDefaultTag', 'par défaut')})</span>
                    )}
                  </span>,
                )}

                {isMondialRelay && definitionRow(
                  t('carrierDetail.defaultEnseigne', 'Enseigne Connect par défaut'),
                  carrier.default_enseigne
                    ? <span className="cd-def-text">{carrier.default_enseigne}</span>
                    : <span className="cd-muted-dash">–</span>,
                )}

                {carrier.web2case_url && definitionRow(
                  t('carrierDetail.web2caseUrl', 'URL du formulaire web transporteur (Web2Case)'),
                  <span>{carrier.web2case_url}</span>,
                )}
                {carrier.form_tracking_patterns?.length > 0 && definitionRow(
                  t('carrierDetail.formRouting', 'Routage formulaire'),
                  <span className="cd-chip-list">
                    {carrier.form_tracking_patterns.map((pattern) => (
                      <span key={pattern} className="cd-chip">{pattern}</span>
                    ))}
                    <span className="cd-def-text">
                      {'→'} {carrier.form_tool}
                    </span>
                  </span>,
                )}
                {Object.keys(carrier.form_defaults || {}).length > 0 && definitionRow(
                  t('carrierDetail.formDefaults', 'Valeurs par défaut du formulaire'),
                  <span className="cd-chip-list">
                    {Object.entries(carrier.form_defaults).map(([key, value]) => (
                      <span key={key} className="cd-chip">{key} = {value}</span>
                    ))}
                  </span>,
                )}

                {carrier.email_routing_rules?.length > 0 && (
                  <div className="cd-routing">
                    <h4>{t('carrierDetail.emailRoutingRules', 'Règles de routage')}</h4>
                    <div className="cd-routing-table">
                      <div className="cd-routing-row cd-routing-head">
                        <span>{t('carrierDetail.matchField', 'Champ')}</span>
                        <span>{t('carrierDetail.matchValue', 'Valeur')}</span>
                        <span>{t('carrierDetail.targetEmail', 'Email cible')}</span>
                      </div>
                      {carrier.email_routing_rules.map((rule, idx) => (
                        <div key={idx} className="cd-routing-row">
                          <span>{rule.match_field}</span>
                          <span>{rule.match_value}</span>
                          <span>{rule.email || rule.email_pattern}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Long texts behind a disclosure */}
                <div className="cd-disclosure">
                  <button
                    type="button"
                    className="cd-disclosure-toggle"
                    onClick={() => setShowContactRules((v) => !v)}
                    aria-expanded={showContactRules}
                  >
                    {showContactRules ? <FaChevronDown /> : <FaChevronRight />}
                    {t('carriersInstructions.contactRules', 'Instructions de contact')}
                    {carrier.contact_rules ? (
                      <span className="cd-flag-ok"><FaCheck aria-hidden="true" /></span>
                    ) : (
                      <span className="cd-flag-warn">
                        <span className="cd-warn-dot" aria-hidden="true" />
                        {t('clientsList.missing', 'manquantes')}
                      </span>
                    )}
                  </button>
                  {showContactRules && (
                    <div className="cd-disclosure-body">
                      {carrier.contact_rules
                        ? <p>{carrier.contact_rules}</p>
                        : <p className="cd-muted-text">{t('carrierDetail.noContactRules', 'Aucune instruction de contact.')}</p>}
                      {carrier.message_instructions && (
                        <>
                          <h4>{t('carrierDetail.messageInstructions', 'Instructions de message')}</h4>
                          <p>{carrier.message_instructions}</p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="cd-edit-form">
                {carrierSaveError && (
                  <div className="cd-alert cd-alert-error">{carrierSaveError}</div>
                )}

                <h3 className="cd-form-section-title">{t('clientEDNDetail.sectionIdentity', 'Identité')}</h3>
                <div className="cd-form-group">
                  <label>{t('carriersInstructions.name', 'Nom')}</label>
                  <input
                    type="text"
                    value={carrierForm.name}
                    onChange={(e) => setCarrierForm((f) => ({ ...f, name: e.target.value }))}
                    className="cd-input"
                  />
                </div>
                <div className="cd-form-group">
                  <label className="cd-inline-toggle">
                    <input
                      type="checkbox"
                      checked={carrierForm.automation || false}
                      onChange={(e) => setCarrierForm((f) => ({ ...f, automation: e.target.checked }))}
                    />
                    {t('carriersList.automationEnabled', 'Automatisation activée pour ce transporteur')}
                  </label>
                </div>
                <div className="cd-form-group">
                  <label className="cd-inline-toggle">
                    <input
                      type="checkbox"
                      checked={carrierForm.is_mail || false}
                      onChange={(e) => setCarrierForm((f) => ({ ...f, is_mail: e.target.checked }))}
                    />
                    {t('carriersList.isMailLabel', 'Transporteur contacté par e-mail')}
                  </label>
                </div>

                <h3 className="cd-form-section-title">{t('carrierDetail.sectionContact', 'Contact')}</h3>
                <div className="cd-form-group">
                  <label>{t('carriersInstructions.emails', 'Emails')}</label>
                  {carrierForm.emails.map((email, index) => (
                    <div key={index} className="cd-email-row">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => handleEmailChange(index, e.target.value)}
                        placeholder={t('carriersInstructions.emailPlaceholder', 'Adresse email')}
                        className="cd-input"
                      />
                      <button type="button" className="cd-remove-btn" onClick={() => handleRemoveEmail(index)} aria-label={t('common.delete', 'Supprimer')}>
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                  <button type="button" className="cd-add-btn" onClick={handleAddEmailField}>
                    <FaPlus /> {t('carriersInstructions.addEmail', 'Ajouter un email')}
                  </button>
                </div>
                <div className="cd-form-group">
                  <label>{t('carrierDetail.ccEmails', 'CC')}</label>
                  {(carrierForm.cc_emails || []).map((cc, index) => (
                    <div key={index} className="cd-email-row">
                      <input
                        type="email"
                        value={cc}
                        onChange={(e) => setCarrierForm((f) => {
                          const updated = [...(f.cc_emails || [])];
                          updated[index] = e.target.value;
                          return { ...f, cc_emails: updated };
                        })}
                        placeholder={t('carrierDetail.ccEmailPlaceholder', 'Adresse en copie')}
                        className="cd-input"
                      />
                      <button type="button" className="cd-remove-btn" onClick={() => setCarrierForm((f) => {
                        const updated = [...(f.cc_emails || [])];
                        updated.splice(index, 1);
                        return { ...f, cc_emails: updated };
                      })} aria-label={t('common.delete', 'Supprimer')}>
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                  <button type="button" className="cd-add-btn" onClick={() => setCarrierForm((f) => ({ ...f, cc_emails: [...(f.cc_emails || []), ''] }))}>
                    <FaPlus /> {t('carrierDetail.addCcEmail', 'Ajouter un CC')}
                  </button>
                </div>
                <div className="cd-form-group">
                  <label htmlFor="cd-email-subject">{t('carrierDetail.emailSubject', 'Objet des e-mails')}</label>
                  <input
                    id="cd-email-subject"
                    type="text"
                    value={carrierForm.email_subject_template || ''}
                    onChange={(e) => setCarrierForm((f) => ({ ...f, email_subject_template: e.target.value }))}
                    maxLength={150}
                    placeholder={DEFAULT_CARRIER_SUBJECT}
                    className="cd-input"
                  />
                  <span className="cd-form-help">
                    {t('carrierDetail.emailSubjectHint', 'Variables : {{vars}}. Vide = format par défaut.', { vars: '{transporteur} {suivi} {objet} {commande}', interpolation: { escapeValue: false } })}
                  </span>
                  <span className="cd-form-help">
                    {t('carrierDetail.emailSubjectPreview', 'Aperçu')} : {previewCarrierSubject(carrierForm.email_subject_template, carrierForm.name || carrier.name)}
                  </span>
                </div>

                <h3 className="cd-form-section-title">{t('clientEDNDetail.sectionInstructions', 'Instructions IA')}</h3>
                <div className="cd-form-group">
                  <label>{t('carriersInstructions.contactRules', 'Instructions de contact')}</label>
                  <textarea
                    value={carrierForm.contact_rules}
                    onChange={(e) => setCarrierForm((f) => ({ ...f, contact_rules: e.target.value }))}
                    rows={5}
                    className="cd-textarea"
                  />
                </div>
                <div className="cd-form-group">
                  <label>{t('carrierDetail.messageInstructions', 'Instructions de message')}</label>
                  <textarea
                    value={carrierForm.message_instructions || ''}
                    onChange={(e) => setCarrierForm((f) => ({ ...f, message_instructions: e.target.value }))}
                    rows={4}
                    maxLength={2000}
                    placeholder={t('carrierDetail.messageInstructionsPlaceholder', 'Instructions appliquées aux messages envoyés à ce transporteur (optionnel). Ajoutées à vos instructions générales.')}
                    className="cd-textarea"
                  />
                  <span className="cd-form-help">
                    {isMondialRelay
                      ? t('carrierDetail.messageInstructionsMondialHint', 'Le numéro de suivi est toujours inclus et les messages Mondial Relay restent sous 255 caractères, quelles que soient ces instructions.')
                      : t('carrierDetail.messageInstructionsHint', 'Le numéro de suivi est toujours inclus dans le message, quelles que soient ces instructions.')}
                  </span>
                </div>

                <h3 className="cd-form-section-title">{t('carrierDetail.sectionAdvanced', 'Avancé')}</h3>
                {isMondialRelay && (
                  <div className="cd-form-group">
                    <label>{t('carrierDetail.defaultEnseigne', 'Enseigne Connect par défaut')}</label>
                    <input
                      type="text"
                      value={carrierForm.default_enseigne || ''}
                      onChange={(e) => setCarrierForm((f) => ({ ...f, default_enseigne: e.target.value }))}
                      placeholder={t('carrierDetail.defaultEnseignePlaceholder', 'Enseigne utilisée quand le client n’a pas d’alias (ex. : votre propre enseigne)')}
                      className="cd-input"
                    />
                    <small className="cd-hint">
                      {t('carrierDetail.defaultEnseigneHint', 'Essayée en dernier, après les alias du client et le nom de l’expéditeur, quand le portail Connect ne liste aucun d’eux.')}
                    </small>
                  </div>
                )}
                <div className="cd-form-group">
                  <label>{t('carrierDetail.formTrackingPatterns', 'Suivis à traiter via formulaire (regex, une par ligne)')}</label>
                  <textarea
                    value={carrierForm.form_tracking_patterns || ''}
                    onChange={(e) => setCarrierForm((f) => ({ ...f, form_tracking_patterns: e.target.value }))}
                    rows={3}
                    className="cd-textarea cd-json-textarea"
                    placeholder={'^JJD'}
                  />
                  <span className="cd-form-help">
                    {t('carrierDetail.formTrackingPatternsHint', "Les numéros de suivi correspondant à ces motifs ne peuvent pas être traités par e-mail : l'IA est redirigée vers l'outil formulaire ci-dessous.")}
                  </span>
                </div>
                <div className="cd-form-group">
                  <label>{t('carrierDetail.formTool', 'Outil formulaire dédié')}</label>
                  <input
                    type="text"
                    value={carrierForm.form_tool || ''}
                    onChange={(e) => setCarrierForm((f) => ({ ...f, form_tool: e.target.value }))}
                    placeholder={t('carrierDetail.formToolPlaceholder', 'Ex. contact_dhl, contact_gls, create_fedex_ticket')}
                    className="cd-input"
                  />
                </div>
                <div className="cd-form-group">
                  <label>{t('carrierDetail.web2caseUrl', 'URL du formulaire web transporteur (Web2Case)')}</label>
                  <input
                    type="text"
                    value={carrierForm.web2case_url || ''}
                    onChange={(e) => setCarrierForm((f) => ({ ...f, web2case_url: e.target.value }))}
                    placeholder="https://reach-cbs.my.site.com/Web2Case/?cp=...&Key=..."
                    className="cd-input"
                  />
                  <span className="cd-form-help">
                    {t('carrierDetail.web2caseUrlHint', "URL (avec la clé de compte) fournie par le transporteur pour ouvrir les demandes via son formulaire web. Utilisée par l'outil formulaire dédié.")}
                  </span>
                </div>
                <div className="cd-form-group">
                  <label>{t('carrierDetail.formDefaults', 'Valeurs par défaut du formulaire')}</label>
                  {(carrierForm.form_defaults || []).map((row, index) => (
                    <div key={index} className="cd-kv-row">
                      <input
                        type="text"
                        value={row.key}
                        onChange={(e) => setCarrierForm((f) => ({
                          ...f,
                          form_defaults: f.form_defaults.map((r, i) => (i === index ? { ...r, key: e.target.value } : r)),
                        }))}
                        placeholder={t('carrierDetail.formDefaultsKey', 'Clé (ex. charger_number)')}
                        className="cd-input"
                      />
                      <input
                        type="text"
                        value={row.value}
                        onChange={(e) => setCarrierForm((f) => ({
                          ...f,
                          form_defaults: f.form_defaults.map((r, i) => (i === index ? { ...r, value: e.target.value } : r)),
                        }))}
                        placeholder={t('carrierDetail.formDefaultsValue', 'Valeur')}
                        className="cd-input"
                      />
                      <button
                        type="button"
                        className="cd-remove-btn"
                        onClick={() => setCarrierForm((f) => ({
                          ...f,
                          form_defaults: f.form_defaults.filter((_, i) => i !== index),
                        }))}
                        aria-label={t('common.remove', 'Retirer')}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="cd-add-btn"
                    onClick={() => setCarrierForm((f) => ({
                      ...f,
                      form_defaults: [...(f.form_defaults || []), { key: '', value: '' }],
                    }))}
                  >
                    + {t('carrierDetail.formDefaultsAdd', 'Ajouter une valeur')}
                  </button>
                  <span className="cd-form-help">
                    {t('carrierDetail.formDefaultsHint', "Données fixes de votre compte pré-remplies dans le formulaire du transporteur, jamais devinées par l'IA. GLS (contact_gls) : charger_number (n° chargeur), shipping_department (code agence), et le contact via « Identité de réclamation » ou les clés email, first_name, last_name, phone, company_name. DHL (contact_dhl) : email du demandeur (sinon le contact de réclamation, sinon l'e-mail du compte).")}
                  </span>
                </div>
                <div className="cd-form-group">
                  <label>{t('carrierDetail.emailRoutingRules', 'Règles de routage')} ({t('carrierDetail.advancedJson', 'JSON')})</label>
                  <textarea
                    value={carrierForm.email_routing_rules || '[]'}
                    onChange={(e) => setCarrierForm((f) => ({ ...f, email_routing_rules: e.target.value }))}
                    rows={6}
                    className="cd-textarea cd-json-textarea"
                    placeholder={'[\n  { "match_field": "sender_postcode_prefix", "match_value": "06", "email": "suivi@carrier.fr" }\n]'}
                  />
                </div>

                <div className="cd-form-actions">
                  <Button variant="primary" onClick={handleSaveCarrier} disabled={carrierSaving}>
                    <FaSave /> {carrierSaving ? t('common.saving', 'Enregistrement...') : t('common.save', 'Enregistrer')}
                  </Button>
                  <Button variant="secondary" onClick={handleCancelEditCarrier} disabled={carrierSaving}>
                    <FaTimes /> {t('common.cancel', 'Annuler')}
                  </Button>
                </div>
              </div>
            )}
          </section>

          {/* ENSEIGNES card — Mondial Relay: account enseignes ↔ client aliases */}
          {isMondialRelay && (
            <section className="cd-card cd-enseignes-card">
              <div className="cd-card-title">
                <h2>{t('carrierDetail.enseignesTitle', 'Enseignes Connect')}</h2>
                <button
                  type="button"
                  className="u-btn u-btn--secondary"
                  onClick={syncEnseignes}
                  disabled={enseigneSync.loading}
                >
                  {enseigneSync.loading
                    ? t('carrierDetail.enseignesSyncing', 'Synchronisation…')
                    : t('carrierDetail.enseignesSync', 'Synchroniser les enseignes')}
                </button>
              </div>
              <p className="cd-muted-text">
                {t('carrierDetail.enseignesHint', 'Lit les enseignes des comptes Connect configurés et propose à quel client chacune correspond. Une réclamation Mondial Relay exige l’enseigne exacte du portail : confirmez les correspondances pour qu’elles soient enregistrées comme alias des clients.')}
                {carrier.enseignes_synced_at && (
                  <> {t('carrierDetail.enseignesSyncedAt', 'Dernière synchronisation : {{date}}', { date: formatDateTime(carrier.enseignes_synced_at) })}</>
                )}
              </p>
              {enseigneSync.error && <div className="cd-alert cd-alert-error">{enseigneSync.error}</div>}
              {enseigneSync.applied && (
                <div className="cd-alert cd-alert-success">
                  {t('carrierDetail.enseignesApplied', '{{count}} correspondance(s) enregistrée(s).', { count: (enseigneSync.applied.applied || []).length })}
                </div>
              )}
              {enseigneSync.data && (
                <>
                  <div className="cd-chip-list">
                    {(enseigneSync.data.accounts || []).map((account) => (
                      <span key={account.label} className={`cd-chip ${account.error ? 'cd-chip-error' : ''}`}>
                        {account.label}: {account.error ? account.error : t('carrierDetail.enseignesCount', '{{count}} enseigne(s)', { count: account.count })}
                      </span>
                    ))}
                  </div>
                  {(enseigneSync.data.enseignes || []).length === 0 ? (
                    <p className="cd-muted-text">{t('carrierDetail.enseignesNone', 'Aucune enseigne lue sur les comptes configurés.')}</p>
                  ) : (
                    <div className="cd-table-wrap">
                      <table className="cd-enseigne-table">
                        <thead>
                          <tr>
                            <th>{t('carrierDetail.enseignesColEnseigne', 'Enseigne Connect')}</th>
                            <th>{t('carrierDetail.enseignesColClient', 'Client')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {enseigneSync.data.enseignes.map((row) => (
                            <tr key={row.enseigne} className={`cd-enseigne-row cd-enseigne-${row.status}`}>
                              <td className="cd-def-text">{row.enseigne}</td>
                              <td>
                                {row.status === 'mapped' ? (
                                  <span className="cd-chip">{row.client_name}</span>
                                ) : (
                                  <select
                                    className="cd-input"
                                    value={enseigneChoices[row.enseigne] || ''}
                                    onChange={(e) => setEnseigneChoices((c) => ({ ...c, [row.enseigne]: e.target.value }))}
                                  >
                                    <option value="">
                                      {row.proposal
                                        ? t('carrierDetail.enseignesProposalHint', 'Proposition : {{name}}', { name: row.proposal.client_name })
                                        : t('carrierDetail.enseignesUnmapped', 'Aucune correspondance proposée')}
                                    </option>
                                    {ednClients.map((client) => (
                                      <option key={client.id} value={client.id}>{client.name}</option>
                                    ))}
                                  </select>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {(enseigneSync.data.clients_without_enseigne || []).length > 0 && (
                    <p className="cd-muted-text">
                      {t('carrierDetail.enseignesClientsWithout', 'Clients sans enseigne sur ces comptes : {{names}}', {
                        names: enseigneSync.data.clients_without_enseigne.map((c) => c.name).join(', ')
                      })}
                    </p>
                  )}
                  <div className="cd-form-actions">
                    <button
                      type="button"
                      className="u-btn u-btn--primary"
                      onClick={applyEnseignes}
                      disabled={enseigneSync.loading || Object.values(enseigneChoices).filter(Boolean).length === 0}
                    >
                      {t('carrierDetail.enseignesApply', 'Enregistrer les correspondances')}
                    </button>
                  </div>
                </>
              )}
            </section>
          )}

          {/* CREDENTIALS card */}
          <CarrierRecipes carrierName={carrier?.name} />

          <section className="cd-card cd-credentials-card">
            <div className="cd-card-title">
              <h2>{t('carrierDetail.credentialAccounts', 'Comptes portail')}</h2>
              <button
                type="button"
                className="u-btn u-btn--secondary"
                onClick={() => {
                  setShowAddCredential(true);
                  setAddCredentialError(null);
                }}
              >
                <FaPlus /> {t('carrierDetail.addCredential', 'Ajouter')}
              </button>
            </div>

            {/* Add credential form */}
            {showAddCredential && (
              <div className="cd-cred-form">
                <h4>{t('carrierDetail.newCredential', 'Nouveau compte')}</h4>
                {addCredentialError && (
                  <div className="cd-alert cd-alert-error">{addCredentialError}</div>
                )}
                <div className="cd-form-group">
                  <label>{t('carrierDetail.label', 'Libellé')}</label>
                  <input
                    type="text"
                    value={newCredential.label}
                    onChange={(e) => setNewCredential((c) => ({ ...c, label: e.target.value }))}
                    placeholder={t('carrierDetail.labelPlaceholder', 'Ex. Principal, Client X')}
                    className="cd-input"
                  />
                </div>
                <div className="cd-form-grid">
                  <div className="cd-form-group">
                    <label>{t('carriersInstructions.username', 'Identifiant')}</label>
                    <input
                      type="text"
                      value={newCredential.username}
                      onChange={(e) => setNewCredential((c) => ({ ...c, username: e.target.value }))}
                      className="cd-input"
                    />
                  </div>
                  <div className="cd-form-group">
                    <label>{t('carriersInstructions.password', 'Mot de passe')}</label>
                    <input
                      type="password"
                      value={newCredential.password}
                      onChange={(e) => setNewCredential((c) => ({ ...c, password: e.target.value }))}
                      className="cd-input"
                    />
                  </div>
                </div>
                <div className="cd-form-group">
                  <label>{t('carrierDetail.mappedClients', 'Clients associés')}</label>
                  {clientChips(newCredential.client_ids, (id) =>
                    toggleClientInList(id, newCredential.client_ids, (ids) =>
                      setNewCredential((c) => ({ ...c, client_ids: ids })),
                    ),
                  )}
                </div>
                {credentialScopeFields(newCredential, setNewCredential)}
                <div className="cd-form-actions">
                  <Button variant="primary" onClick={handleAddCredential} disabled={addingCredential || !newCredential.username || !newCredential.password}>
                    <FaSave /> {addingCredential ? t('common.saving', 'Enregistrement...') : t('common.create', 'Créer')}
                  </Button>
                  <Button variant="secondary" onClick={() => setShowAddCredential(false)} disabled={addingCredential}>
                    <FaTimes /> {t('common.cancel', 'Annuler')}
                  </Button>
                </div>
              </div>
            )}

            {/* Credentials list */}
            {credentialsLoading ? (
              <div className="cd-skeleton" aria-busy="true">
                <div className="cd-skeleton-row" />
                <div className="cd-skeleton-row" />
              </div>
            ) : credentials.length === 0 && !showAddCredential ? (
              <div className="cd-state cd-state-empty">
                <FaKey />
                <p>{t('carrierDetail.noCredentials', 'Aucun compte portail configuré')}</p>
              </div>
            ) : (
              <div className="cd-cred-list">
                {credentials.map((cred) => (
                  <div key={cred.id} className="cd-cred-item">
                    {editingCredentialId === cred.id ? (
                      <div className="cd-cred-form">
                        <h4>{t('carrierDetail.editCredential', 'Modifier le compte')}</h4>
                        {editCredentialError && (
                          <div className="cd-alert cd-alert-error">{editCredentialError}</div>
                        )}
                        <div className="cd-form-group">
                          <label>{t('carrierDetail.label', 'Libellé')}</label>
                          <input
                            type="text"
                            value={editCredentialForm.label}
                            onChange={(e) => setEditCredentialForm((f) => ({ ...f, label: e.target.value }))}
                            className="cd-input"
                          />
                        </div>
                        <div className="cd-form-grid">
                          <div className="cd-form-group">
                            <label>{t('carriersInstructions.username', 'Identifiant')}</label>
                            <input
                              type="text"
                              value={editCredentialForm.username}
                              onChange={(e) => setEditCredentialForm((f) => ({ ...f, username: e.target.value }))}
                              className="cd-input"
                            />
                          </div>
                          <div className="cd-form-group">
                            <label>{t('carriersInstructions.password', 'Mot de passe')}</label>
                            <input
                              type="password"
                              value={editCredentialForm.password}
                              onChange={(e) => setEditCredentialForm((f) => ({ ...f, password: e.target.value }))}
                              placeholder={t('carrierDetail.leaveBlank', 'laisser vide pour conserver')}
                              className="cd-input"
                            />
                          </div>
                        </div>
                        <div className="cd-form-group">
                          <label>{t('carrierDetail.mappedClients', 'Clients associés')}</label>
                          {clientChips(editCredentialForm.client_ids, (id) =>
                            toggleClientInList(id, editCredentialForm.client_ids, (ids) =>
                              setEditCredentialForm((f) => ({ ...f, client_ids: ids })),
                            ),
                          )}
                        </div>
                        {credentialScopeFields(editCredentialForm, setEditCredentialForm)}
                        <div className="cd-form-actions">
                          <Button variant="primary" onClick={() => handleSaveCredential(cred.id)} disabled={savingCredential}>
                            <FaSave /> {savingCredential ? t('common.saving', 'Enregistrement...') : t('common.save', 'Enregistrer')}
                          </Button>
                          <Button variant="secondary" onClick={handleCancelEditCredential} disabled={savingCredential}>
                            <FaTimes /> {t('common.cancel', 'Annuler')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="cd-cred-head">
                          <div className="cd-cred-title">
                            <FaKey aria-hidden="true" />
                            <strong>{cred.label || cred.username}</strong>
                            {cred.is_valid ? (
                              <span className="cd-cred-badge valid">{t('carrierDetail.valid', 'Valide')}</span>
                            ) : (
                              <span className="cd-cred-badge invalid">{t('carrierDetail.invalid', 'Invalide')}</span>
                            )}
                          </div>
                          <div className="cd-cred-actions">
                            <button type="button" className="cd-icon-btn" onClick={() => handleStartEditCredential(cred)} aria-label={t('carrierDetail.editCredential', 'Modifier le compte')}>
                              <FaEdit />
                            </button>
                            <button type="button" className="cd-icon-btn cd-icon-btn--danger" onClick={() => handleDeleteCredential(cred)} aria-label={t('common.delete', 'Supprimer')}>
                              <FaTrash />
                            </button>
                          </div>
                        </div>
                        <div className="cd-cred-meta">
                          <span>{cred.username}</span>
                          {cred.last_used && (
                            <span>{t('carrierDetail.lastUsed', 'Dernière utilisation')} : {formatDateTime(cred.last_used)}</span>
                          )}
                          {cred.scope?.countries?.length > 0 && (
                            <span><FaGlobe aria-hidden="true" /> {scopeLabel(cred.scope)}</span>
                          )}
                        </div>
                        <div className="cd-cred-clients">
                          <FaUsers aria-hidden="true" />
                          {cred.client_ids?.length > 0 ? (
                            <span className="cd-chip-list">
                              {cred.client_ids.map((cid) => (
                                <span key={cid} className="cd-chip">{getClientName(cid)}</span>
                              ))}
                            </span>
                          ) : (
                            <span className="cd-muted-text">{t('carrierDetail.defaultAccount', 'Par défaut (tous les clients)')}</span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* OTP mailbox: portal login verification codes (Colissimo, Mondial Relay…) */}
          <section className="cd-card cd-otp-card">
            <div className="cd-card-title">
              <h2><FaShieldAlt aria-hidden="true" /> {t('carrierDetail.otpMailboxTitle', 'Boîte mail OTP')}</h2>
              {otpMailbox?.configured && (
                <span className="cd-card-title-meta">{otpMailbox.email}</span>
              )}
            </div>
            <div className="cd-otp-body">
            <p className="cd-form-help cd-otp-help">
              {t(
                'carrierDetail.otpMailboxHelp',
                "Si le portail de ce transporteur exige un code de vérification envoyé par email à la connexion (double authentification), connectez ici la boîte qui reçoit ces codes. Elle est utilisée uniquement pour lire les codes de connexion — jamais pour les tickets.",
              )}
            </p>

            {otpNotice && (
              <div className={`cd-alert ${otpNotice.type === 'error' ? 'cd-alert-error' : 'cd-alert-success'}`}>
                {otpNotice.text}
              </div>
            )}
            {otpError && <div className="cd-alert cd-alert-error">{otpError}</div>}

            {otpMailboxLoading ? (
              <div className="cd-skeleton" aria-busy="true">
                <div className="cd-skeleton-row" />
              </div>
            ) : otpMailbox?.configured ? (
              <div className="cd-cred-list">
                <div className="cd-cred-item">
                  <div className="cd-cred-head">
                    <div className="cd-cred-title">
                      {otpMailbox.provider === 'outlook' ? <SiMicrosoftoutlook aria-hidden="true" /> : <FaEnvelope aria-hidden="true" />}
                      <strong>{otpMailbox.email}</strong>
                      <span className="cd-cred-badge valid">
                        {otpMailbox.provider === 'outlook'
                          ? t('carrierDetail.otpProviderOutlook', 'Outlook')
                          : otpMailbox.provider === 'linked_outlook'
                          ? t('carrierDetail.otpProviderLinkedOutlook', 'Outlook (compte connecté)')
                          : otpMailbox.provider === 'linked_google'
                          ? t('carrierDetail.otpProviderLinkedGoogle', 'Gmail (compte connecté)')
                          : otpMailbox.provider === 'linked_account_mailbox'
                          ? t('carrierDetail.otpProviderAccountMailbox', 'Boîte Gmail du compte')
                          : t('carrierDetail.otpProviderImap', "Gmail / IMAP")}
                      </span>
                    </div>
                    <div className="cd-cred-actions">
                      <button
                        type="button"
                        className="cd-icon-btn cd-icon-btn--danger"
                        onClick={handleDisconnectOtpMailbox}
                        aria-label={t('carrierDetail.otpDisconnect', 'Déconnecter')}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                  <div className="cd-cred-meta">
                    <span>
                      {t('carrierDetail.otpConnectedAt', 'Connectée le')} : {formatDateTime(otpMailbox.connected_at)}
                    </span>
                  </div>
                </div>
              </div>
            ) : showOtpImapForm ? (
              <div className="cd-cred-form">
                <h4>{t('carrierDetail.otpImapTitle', "Connecter une boîte Gmail (mot de passe d'application)")}</h4>
                <div className="cd-form-grid">
                  <div className="cd-form-group">
                    <label>{t('carrierDetail.otpImapEmail', 'Adresse email')}</label>
                    <input
                      type="email"
                      value={otpImapForm.email}
                      onChange={(e) => setOtpImapForm((f) => ({ ...f, email: e.target.value }))}
                      className="cd-input"
                    />
                  </div>
                  <div className="cd-form-group">
                    <label>{t('carrierDetail.otpImapPassword', "Mot de passe d'application")}</label>
                    <div className="cd-input-with-toggle">
                      <input
                        type={showOtpPassword ? 'text' : 'password'}
                        value={otpImapForm.password}
                        onChange={(e) => setOtpImapForm((f) => ({ ...f, password: e.target.value }))}
                        className="cd-input"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="cd-icon-btn"
                        onClick={() => setShowOtpPassword((v) => !v)}
                        aria-label={t('carrierDetail.otpTogglePassword', 'Afficher / masquer')}
                      >
                        {showOtpPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="cd-form-grid">
                  <div className="cd-form-group">
                    <label>{t('carrierDetail.otpImapServer', 'Serveur IMAP')}</label>
                    <input
                      type="text"
                      value={otpImapForm.imap_server}
                      onChange={(e) => setOtpImapForm((f) => ({ ...f, imap_server: e.target.value }))}
                      className="cd-input"
                    />
                  </div>
                  <div className="cd-form-group">
                    <label>{t('carrierDetail.otpImapPort', 'Port IMAP')}</label>
                    <input
                      type="number"
                      value={otpImapForm.imap_port}
                      onChange={(e) => setOtpImapForm((f) => ({ ...f, imap_port: Number(e.target.value) || 993 }))}
                      className="cd-input"
                    />
                  </div>
                </div>
                <p className="cd-form-help">
                  {t('carrierDetail.otpImapHelp', "Pour Gmail, créez un mot de passe d'application (Compte Google > Sécurité > Mots de passe des applications) — pas votre mot de passe habituel.")}{' '}
                  <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noreferrer">
                    {t('carrierDetail.otpImapHelpLink', "Guide Google")}
                  </a>
                </p>
                <div className="cd-form-actions">
                  <Button
                    variant="primary"
                    onClick={handleSaveOtpImap}
                    disabled={otpSaving || !otpImapForm.email || !otpImapForm.password}
                  >
                    <FaSave /> {otpSaving ? t('carrierDetail.otpVerifying', 'Vérification...') : t('common.save', 'Enregistrer')}
                  </Button>
                  <Button variant="secondary" onClick={() => { setShowOtpImapForm(false); setOtpError(null); }} disabled={otpSaving}>
                    <FaTimes /> {t('common.cancel', 'Annuler')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="cd-otp-connect-options">
                {otpLinkableAccounts.length > 0 && (
                  <div className="cd-otp-linkable">
                    <p className="cd-form-help">
                      {t(
                        'carrierDetail.otpLinkableHelp',
                        'Réutilisez un compte déjà connecté — aucune nouvelle connexion nécessaire :',
                      )}
                    </p>
                    {otpLinkableAccounts.map((account) => (
                      <button
                        key={`${account.provider}:${account.email}`}
                        type="button"
                        className="u-btn u-btn--secondary cd-otp-linkable-btn"
                        onClick={() => handleLinkOtpAccount(account)}
                        disabled={otpLinking !== null}
                      >
                        {account.provider === 'linked_outlook'
                          ? <SiMicrosoftoutlook aria-hidden="true" />
                          : <FaEnvelope aria-hidden="true" />}{' '}
                        {otpLinking === `${account.provider}:${account.email}`
                          ? t('common.loading', 'Chargement...')
                          : t('carrierDetail.otpLinkUse', 'Utiliser {{email}}', { email: account.email })}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  className="u-btn u-btn--secondary"
                  onClick={handleConnectOtpOutlook}
                  disabled={otpOutlookLoading}
                >
                  <SiMicrosoftoutlook aria-hidden="true" />{' '}
                  {otpOutlookLoading
                    ? t('common.loading', 'Chargement...')
                    : t('carrierDetail.otpConnectOutlook', 'Connecter un compte Outlook')}
                </button>
                <button
                  type="button"
                  className="u-btn u-btn--secondary"
                  onClick={() => { setShowOtpImapForm(true); setOtpError(null); }}
                >
                  <FaEnvelope aria-hidden="true" /> {t('carrierDetail.otpConnectImap', 'Connecter une boîte Gmail (mot de passe d\'application)')}
                </button>
              </div>
            )}
            </div>
          </section>

          {isClaimFormCarrier && (
            <section className="cd-card cd-claim-card">
              <div className="cd-card-title">
                <h2><FaUniversity aria-hidden="true" /> {t('carrierDetail.claimIdentityTitle', 'Identité de réclamation')}</h2>
                {carrier?.claim_bank?.configured && (
                  <span className="cd-card-title-meta">
                    {t('carrierDetail.claimBankConfigured', 'IBAN •••• {{last4}}', { last4: carrier.claim_bank.iban_last4 })}
                  </span>
                )}
              </div>
              <p className="cd-form-help">
                {t(
                  'carrierDetail.claimIdentityHelp',
                  "Contact et coordonnées bancaires utilisés pour remplir les formulaires de réclamation de ce transporteur (remboursements). L'IBAN est chiffré et n'est jamais réaffiché en clair.",
                )}
              </p>
              {claimNotice && (
                <div className={`cd-alert ${claimNotice.type === 'error' ? 'cd-alert-error' : 'cd-alert-success'}`}>
                  {claimNotice.text}
                </div>
              )}
              {!showClaimForm && (
                carrier?.claim_contact ? (
                  <div>
                    <div className="cd-def-row">
                      <span className="cd-def-label">{t('carrierDetail.claimContactLabel', 'Contact')}</span>
                      <span className="cd-def-value">
                        {carrier.claim_contact.first_name} {carrier.claim_contact.last_name} · {carrier.claim_contact.email} · {carrier.claim_contact.phone_number}
                      </span>
                    </div>
                    {carrier.claim_contact.account_number && (
                      <div className="cd-def-row">
                        <span className="cd-def-label">{t('carrierDetail.claimAccountLabel', 'N° de compte transporteur')}</span>
                        <span className="cd-def-value">{carrier.claim_contact.account_number}</span>
                      </div>
                    )}
                    <div className="cd-def-row">
                      <span className="cd-def-label">{t('carrierDetail.claimBankLabel', 'Banque')}</span>
                      <span className="cd-def-value">
                        {carrier.claim_bank?.configured
                          ? `${carrier.claim_bank.bank_name} · ${carrier.claim_bank.bic} · IBAN •••• ${carrier.claim_bank.iban_last4} (${carrier.claim_bank.account_holder_name})`
                          : t('carrierDetail.claimBankMissing', 'Non configurée')}
                      </span>
                    </div>
                    <div className="cd-cred-actions" style={{ marginTop: 12 }}>
                      <button type="button" className="u-btn u-btn--secondary" onClick={openClaimForm}>
                        {t('common.edit', 'Modifier')}
                      </button>
                      <button type="button" className="u-btn u-btn--secondary" onClick={handleClearClaim} disabled={claimSaving}>
                        {t('carrierDetail.claimClear', 'Supprimer')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button type="button" className="u-btn u-btn--secondary" onClick={openClaimForm}>
                    {t('carrierDetail.claimConfigure', "Configurer l'identité de réclamation")}
                  </button>
                )
              )}
              {showClaimForm && (
                <div>
                  <div className="cd-form-grid">
                    <div className="cd-form-group">
                      <label>{t('carrierDetail.claimContactAs', 'Déclarant')}</label>
                      <select
                        className="cd-input"
                        value={claimForm.contact_as}
                        onChange={(e) => setClaimForm({ ...claimForm, contact_as: e.target.value })}
                      >
                        <option value="sender">{t('carrierDetail.claimContactAsSender', 'Expéditeur')}</option>
                        <option value="recipient">{t('carrierDetail.claimContactAsRecipient', 'Destinataire')}</option>
                        <option value="other">{t('carrierDetail.claimContactAsOther', 'Autre')}</option>
                      </select>
                    </div>
                    {[
                      ['first_name', t('carrierDetail.claimFirstName', 'Prénom'), true],
                      ['last_name', t('carrierDetail.claimLastName', 'Nom'), true],
                      ['email', t('carrierDetail.claimEmail', 'Email'), true],
                      ['phone_number', t('carrierDetail.claimPhone', 'Téléphone'), true],
                      ['account_number', t('carrierDetail.claimAccountNumber', 'N° de compte transporteur'), false],
                      ['company_name', t('carrierDetail.claimCompany', 'Société'), false],
                      ['address_line1', t('carrierDetail.claimAddress1', 'Adresse'), false],
                      ['postal_code', t('carrierDetail.claimPostalCode', 'Code postal'), false],
                      ['city', t('carrierDetail.claimCity', 'Ville'), false],
                    ].map(([field, label, required]) => (
                      <div className="cd-form-group" key={field}>
                        <label>{label}{required ? ' *' : ''}</label>
                        <input
                          type="text"
                          className="cd-input"
                          value={claimForm[field]}
                          onChange={(e) => setClaimForm({ ...claimForm, [field]: e.target.value })}
                        />
                      </div>
                    ))}
                    <div className="cd-form-group">
                      <label>{t('carrierDetail.claimIban', 'IBAN')} *</label>
                      <input
                        type="text"
                        className="cd-input"
                        placeholder={carrier?.claim_bank?.configured
                          ? t('carrierDetail.claimIbanPlaceholder', '•••• {{last4}} — ressaisir pour modifier', { last4: carrier.claim_bank.iban_last4 })
                          : 'FR76 ...'}
                        value={claimForm.iban}
                        onChange={(e) => setClaimForm({ ...claimForm, iban: e.target.value })}
                      />
                    </div>
                    {[
                      ['bic', t('carrierDetail.claimBic', 'BIC'), true],
                      ['bank_name', t('carrierDetail.claimBankName', 'Banque'), true],
                      ['account_holder_name', t('carrierDetail.claimHolder', 'Titulaire du compte'), true],
                    ].map(([field, label, required]) => (
                      <div className="cd-form-group" key={field}>
                        <label>{label}{required ? ' *' : ''}</label>
                        <input
                          type="text"
                          className="cd-input"
                          value={claimForm[field]}
                          onChange={(e) => setClaimForm({ ...claimForm, [field]: e.target.value })}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="cd-cred-actions" style={{ marginTop: 12 }}>
                    <button type="button" className="u-btn u-btn--primary" onClick={handleSaveClaim} disabled={claimSaving}>
                      {claimSaving ? t('common.loading', 'Chargement...') : t('common.save', 'Enregistrer')}
                    </button>
                    <button type="button" className="u-btn u-btn--secondary" onClick={() => setShowClaimForm(false)} disabled={claimSaving}>
                      {t('common.cancel', 'Annuler')}
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default CarrierDetail;
